import type { CampaignAnalysisHistoryItem } from '@/entities/campaign/generated/campaign-type';
import type {
  CampaignAnalysisFailureReason,
  CampaignAnalysisStatus,
} from '@/entities/campaign/generated/campaign-type';
import { Badge } from '@/shared/components/shadcn/badge';
import { Button } from '@/shared/components/shadcn/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/shadcn/card';
import { formatDateTime } from '@/shared/utils/date/date-formats';
import { FileChartColumn, Loader2 } from 'lucide-react';

import {
  getCampaignAnalysisRunTitle,
  getCampaignAnalysisStatusLabel,
  getCampaignAnalysisTargetMetricLabel,
} from '../helpers/analysis-history';

type Props = {
  failureReason?: CampaignAnalysisFailureReason | null;
  isStatusError?: boolean;
  onRetryStatus?: () => void;
  run: CampaignAnalysisHistoryItem;
  status?: CampaignAnalysisStatus | null;
};

export function CampaignAnalysisRunPlaceholder({
  failureReason,
  isStatusError = false,
  onRetryStatus,
  run,
  status = run.status,
}: Props) {
  const isRunning = status === 'requesting' || status === 'processing';
  const postCount = run.post_count?.toString() || '-';

  const statusContent = getRunStatusContent(status, failureReason, isStatusError);

  return (
    <Card className="border-border bg-card min-w-0 overflow-hidden">
      <CardHeader className="border-border border-b p-6">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-2xl font-semibold tracking-tight">
            {getCampaignAnalysisRunTitle(run)}
          </CardTitle>
          <Badge variant={isRunning ? 'default' : 'outline'}>
            {getCampaignAnalysisStatusLabel(status ?? run.status)}
          </Badge>
        </div>
        <CardDescription className="mt-2 text-sm leading-6">
          Requested by {run.requested_by.first_name} {run.requested_by.last_name}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="grid gap-0 px-6 py-8 lg:grid-cols-[minmax(0,1fr)_260px]">
          <div className="flex min-h-32 items-start gap-4 py-2 lg:pr-8">
            <div className="bg-muted/50 flex size-10 shrink-0 items-center justify-center rounded-lg">
              {isRunning ? (
                <span aria-label="Generating analysis report" role="status">
                  <Loader2
                    aria-hidden="true"
                    className="text-muted-foreground size-5 animate-spin"
                  />
                </span>
              ) : (
                <FileChartColumn className="text-muted-foreground size-5" aria-hidden="true" />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-foreground text-sm font-medium">{statusContent.title}</p>
              <p className="text-muted-foreground mt-1 text-sm leading-6">
                {statusContent.description}
              </p>
              {isStatusError && onRetryStatus ? (
                <Button className="mt-4" onClick={onRetryStatus} size="sm" variant="outline">
                  Retry status
                </Button>
              ) : null}
            </div>
          </div>
          <div className="border-border grid gap-3 border-t pt-6 lg:border-t-0 lg:border-l lg:pt-0 lg:pl-8">
            <AnalysisRunMeta
              label="Target metric"
              value={getCampaignAnalysisTargetMetricLabel(run.target_metric)}
            />
            <AnalysisRunMeta label="Posts" value={postCount} />
            <AnalysisRunMeta label="Started" value={formatDateTime(run.created_at)} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function getRunStatusContent(
  status: CampaignAnalysisStatus | null,
  failureReason?: CampaignAnalysisFailureReason | null,
  isStatusError = false,
) {
  if (isStatusError) {
    return {
      title: 'Status unavailable',
      description: 'The latest progress could not be loaded. Retry to continue tracking this run.',
    };
  }

  if (status === 'requesting') {
    return {
      title: 'Analysis requested',
      description: 'The request has been received and is waiting to start.',
    };
  }

  if (status === 'processing') {
    return {
      title: 'Generating the report',
      description: 'Campaign data is being analyzed for this report.',
    };
  }

  if (status === 'failed') {
    return {
      title: 'Analysis failed',
      description: getFailureReasonDescription(failureReason),
    };
  }

  return {
    title: 'Analysis completed',
    description: 'This analysis run completed successfully.',
  };
}

function getFailureReasonDescription(failureReason?: CampaignAnalysisFailureReason | null) {
  if (failureReason === 'timeout') {
    return 'The analysis did not finish within the allowed time.';
  }

  if (failureReason === 'optimizer_failed') {
    return 'The report could not be generated from the analyzed campaign data.';
  }

  if (failureReason === 'submit_failed') {
    return 'The analysis request could not be submitted for processing.';
  }

  return 'This analysis run did not complete.';
}

function AnalysisRunMeta({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground text-right font-medium">{value}</span>
    </div>
  );
}
